import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  DndContext,
  DragOverlay,
  useSensor,
  useSensors,
  PointerSensor,
  closestCenter,
} from '@dnd-kit/core';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Plus,
  Bot,
  Clock,
  Target,
  Bug,
  Zap,
  FlaskConical,
  Rocket,
  MoreHorizontal,
  Trash2,
  Edit,
  Link,
  Sparkles,
  Moon,
  Sun,
  Settings,
  User,
  GripVertical,
  AlertCircle,
  CheckCircle,
  FileCode,
  TestTube,
  ListChecks,
  FileText,
  ExternalLink,
  Save,
  Copy,
  X
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const getHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('access_token') || localStorage.getItem('token')}`
});

// Task type config
const TASK_TYPES = {
  feature: { label: 'Feature', icon: Rocket, color: 'bg-indigo-500' },
  testing: { label: 'Testing', icon: FlaskConical, color: 'bg-cyan-500' },
  bug_fix: { label: 'Bug Fix', icon: Bug, color: 'bg-red-500' },
  upgrade: { label: 'Upgrade', icon: Zap, color: 'bg-amber-500' },
  planning: { label: 'Planning', icon: Target, color: 'bg-purple-500' },
  review: { label: 'Review', icon: Edit, color: 'bg-green-500' },
  deployment: { label: 'Deploy', icon: Rocket, color: 'bg-emerald-500' },
  other: { label: 'Other', icon: Clock, color: 'bg-slate-500' }
};

const PRIORITIES = {
  low: { label: 'Low', color: 'bg-slate-400' },
  medium: { label: 'Medium', color: 'bg-blue-500' },
  high: { label: 'High', color: 'bg-orange-500' },
  critical: { label: 'Critical', color: 'bg-red-600' }
};

const TASK_STATUSES = {
  scheduled: { label: 'Scheduled', color: 'text-slate-400' },
  in_progress: { label: 'In Progress', color: 'text-blue-500' },
  completed: { label: 'Completed', color: 'text-green-500' },
  cancelled: { label: 'Cancelled', color: 'text-slate-400 line-through' },
  blocked: { label: 'Blocked', color: 'text-red-500' }
};

// Time slots from 12:30 PM to 11:30 PM
const generateTimeSlots = (startTime = "12:30", endTime = "23:30", afterHoursStart = "21:30") => {
  const slots = [];
  const [startHour, startMin] = startTime.split(':').map(Number);
  const [endHour, endMin] = endTime.split(':').map(Number);
  const [afterHour, afterMin] = afterHoursStart.split(':').map(Number);
  
  let hour = startHour;
  let min = startMin;
  
  while (hour < endHour || (hour === endHour && min <= endMin)) {
    const timeStr = `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
    const isAfterHours = hour > afterHour || (hour === afterHour && min >= afterMin);
    slots.push({ time: timeStr, isAfterHours });
    
    min += 30;
    if (min >= 60) {
      min = 0;
      hour++;
    }
  }
  
  return slots;
};

const formatTime = (timeStr) => {
  if (!timeStr) return '';
  const [hour, min] = timeStr.split(':').map(Number);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
  return `${displayHour}:${min.toString().padStart(2, '0')} ${ampm}`;
};

const formatDateShort = (dateStr) => {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthIndex = parseInt(parts[1], 10) - 1;
  const day = parseInt(parts[2], 10);
  return `${months[monthIndex]} ${day}`;
};

export default function DevCalendar() {
  // State
  const [view, setView] = useState('weekly'); // 'weekly' or 'daily'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [tasks, setTasks] = useState([]);
  const [tasksByDate, setTasksByDate] = useState({});
  const [agents, setAgents] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [scopeItems, setScopeItems] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [settings, setSettings] = useState({
    default_start_time: "12:30",
    default_end_time: "21:30",
    after_hours_start: "21:30",
    after_hours_end: "23:59"
  });
  const [loading, setLoading] = useState(true);
  
  // Drag and drop state
  const [activeTask, setActiveTask] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  
  // Configure drag sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Minimum drag distance before activating
      },
    })
  );
  
  // Dialogs
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [showAgentDialog, setShowAgentDialog] = useState(false);
  const [showSettingsDialog, setShowSettingsDialog] = useState(false);
  const [showTaskDetailsDialog, setShowTaskDetailsDialog] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [editingAgent, setEditingAgent] = useState(null);
  const [viewingTask, setViewingTask] = useState(null);
  const [taskActivityLogs, setTaskActivityLogs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [activeTab, setActiveTab] = useState('definition');
  
  // Task form with all specification fields
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    task_type: 'feature',
    priority: 'medium',
    date: '',
    start_time: '12:30',
    end_time: '14:00',
    assigned_agent_id: '',
    scope_item_id: '',
    timeline_milestone_id: '',
    auto_create_scope: false,
    // Definition Tab
    acceptance_criteria: '',
    expected_outcome: '',
    // Technical Tab
    technical_requirements: '',
    related_files: [],
    ui_requirements: '',
    // Testing Tab
    testing_instructions: '',
    test_scenarios: '',
    success_indicators: '',
    // Context Tab
    dependencies_notes: '',
    reference_links: []
  });
  
  // Agent form
  const [agentForm, setAgentForm] = useState({
    name: '',
    description: '',
    specialty: 'general',
    avatar_color: '#6366f1',
    work_hours: {
      start_time: '12:30',
      end_time: '21:30',
      days_of_week: [1, 2, 3, 4, 5]
    },
    external_id: '',
    external_type: ''
  });

  // Get week dates
  const getWeekDates = useCallback((date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Sunday
    const monday = new Date(d.setDate(diff));
    
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const dayDate = new Date(monday);
      dayDate.setDate(monday.getDate() + i);
      dates.push(dayDate);
    }
    return dates;
  }, []);

  const weekDates = getWeekDates(currentDate);
  const weekStart = weekDates[0].toISOString().split('T')[0];

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch week tasks
      const tasksRes = await axios.get(
        `${API}/api/dev-calendar/tasks/week/${weekStart}`,
        { headers: getHeaders() }
      );
      setTasks(tasksRes.data.all_tasks || []);
      setTasksByDate(tasksRes.data.tasks_by_date || {});
      setAgents(tasksRes.data.agents || []);
      
      // Fetch suggestions
      const suggestionsRes = await axios.get(
        `${API}/api/dev-calendar/suggestions`,
        { headers: getHeaders() }
      );
      setSuggestions(suggestionsRes.data.suggestions || []);
      
      // Fetch linkable items and templates
      const [scopeRes, milestonesRes, settingsRes, templatesRes] = await Promise.all([
        axios.get(`${API}/api/dev-calendar/linkable/scope-items`, { headers: getHeaders() }),
        axios.get(`${API}/api/dev-calendar/linkable/timeline-milestones`, { headers: getHeaders() }),
        axios.get(`${API}/api/dev-calendar/settings`, { headers: getHeaders() }),
        axios.get(`${API}/api/dev-calendar/templates`, { headers: getHeaders() })
      ]);
      
      setScopeItems(scopeRes.data.items || []);
      setMilestones(milestonesRes.data.milestones || []);
      setSettings(settingsRes.data.settings || settings);
      setTemplates(templatesRes.data.templates || []);
      
    } catch (error) {
      console.error('Error fetching calendar data:', error);
      toast.error('Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  }, [weekStart]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Navigation
  const navigateWeek = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + (direction * 7));
    setCurrentDate(newDate);
  };

  const navigateDay = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(newDate.getDate() + direction);
    setCurrentDate(newDate);
  };

  const goToToday = () => setCurrentDate(new Date());

  // Task CRUD
  const handleCreateTask = async () => {
    try {
      const endpoint = taskForm.auto_create_scope 
        ? `${API}/api/dev-calendar/tasks/auto-create-scope`
        : `${API}/api/dev-calendar/tasks`;
      
      await axios.post(endpoint, taskForm, { headers: getHeaders() });
      toast.success('Task created');
      setShowTaskDialog(false);
      resetTaskForm();
      fetchData();
    } catch (error) {
      console.error('Error creating task:', error);
      toast.error('Failed to create task');
    }
  };

  const handleUpdateTask = async () => {
    if (!editingTask) return;
    try {
      await axios.patch(
        `${API}/api/dev-calendar/tasks/${editingTask.id}`,
        taskForm,
        { headers: getHeaders() }
      );
      toast.success('Task updated');
      setShowTaskDialog(false);
      setEditingTask(null);
      resetTaskForm();
      fetchData();
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Failed to update task');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Delete this task?')) return;
    try {
      await axios.delete(`${API}/api/dev-calendar/tasks/${taskId}`, { headers: getHeaders() });
      toast.success('Task deleted');
      fetchData();
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Failed to delete task');
    }
  };

  // View task details
  const openTaskDetails = async (task) => {
    setViewingTask(task);
    setShowTaskDetailsDialog(true);
    
    // Fetch activity logs for this task
    try {
      const response = await axios.get(
        `${API}/api/agent-api/activity-logs?task_id=${task.id}`,
        { headers: { 'X-Agent-Token': 'sage-rhino1-dev-token-2026' } }
      );
      setTaskActivityLogs(response.data.logs || []);
    } catch (error) {
      console.log('No activity logs available');
      setTaskActivityLogs([]);
    }
  };

  const handleTaskStatusChange = async (taskId, status) => {
    try {
      await axios.patch(
        `${API}/api/dev-calendar/tasks/${taskId}`,
        { status },
        { headers: getHeaders() }
      );
      toast.success('Status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  // Drag and drop handlers
  const handleDragStart = (event) => {
    const { active } = event;
    const task = tasks.find(t => t.id === active.id);
    setActiveTask(task);
    setIsDragging(true);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveTask(null);
    setIsDragging(false);
    
    if (!over) return;
    
    // Parse drop target: format is "date-time" e.g., "2026-02-25-14:30"
    const dropTarget = over.id;
    if (!dropTarget || typeof dropTarget !== 'string') return;
    
    const parts = dropTarget.split('-');
    if (parts.length < 4) return;
    
    const newDate = `${parts[0]}-${parts[1]}-${parts[2]}`;
    const newTime = parts[3];
    
    const task = tasks.find(t => t.id === active.id);
    if (!task) return;
    
    // Skip if dropped in same slot
    if (task.date === newDate && task.start_time === newTime) return;
    
    // Calculate duration to maintain same end time offset
    const [startHour, startMin] = task.start_time.split(':').map(Number);
    const [endHour, endMin] = task.end_time.split(':').map(Number);
    const durationMinutes = (endHour * 60 + endMin) - (startHour * 60 + startMin);
    
    const [newStartHour, newStartMin] = newTime.split(':').map(Number);
    const newEndMinutes = newStartHour * 60 + newStartMin + durationMinutes;
    const newEndHour = Math.floor(newEndMinutes / 60);
    const newEndMin = newEndMinutes % 60;
    const newEndTime = `${newEndHour.toString().padStart(2, '0')}:${newEndMin.toString().padStart(2, '0')}`;
    
    try {
      await axios.patch(
        `${API}/api/dev-calendar/tasks/${task.id}`,
        { date: newDate, start_time: newTime, end_time: newEndTime },
        { headers: getHeaders() }
      );
      toast.success(`Task moved to ${formatDateShort(newDate)} at ${formatTime(newTime)}`);
      fetchData();
    } catch (error) {
      console.error('Error moving task:', error);
      toast.error('Failed to move task');
    }
  };

  const handleDragCancel = () => {
    setActiveTask(null);
    setIsDragging(false);
  };

  // Agent CRUD
  const handleCreateAgent = async () => {
    try {
      await axios.post(`${API}/api/dev-calendar/agents`, agentForm, { headers: getHeaders() });
      toast.success('Agent created');
      setShowAgentDialog(false);
      resetAgentForm();
      fetchData();
    } catch (error) {
      console.error('Error creating agent:', error);
      toast.error('Failed to create agent');
    }
  };

  const handleUpdateAgent = async () => {
    if (!editingAgent) return;
    try {
      await axios.patch(
        `${API}/api/dev-calendar/agents/${editingAgent.id}`,
        agentForm,
        { headers: getHeaders() }
      );
      toast.success('Agent updated');
      setShowAgentDialog(false);
      setEditingAgent(null);
      resetAgentForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to update agent');
    }
  };

  const handleDeleteAgent = async (agentId) => {
    if (!window.confirm('Delete this agent?')) return;
    try {
      await axios.delete(`${API}/api/dev-calendar/agents/${agentId}`, { headers: getHeaders() });
      toast.success('Agent deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete agent');
    }
  };

  // Suggestions
  const handleGenerateSuggestions = async () => {
    try {
      const res = await axios.post(
        `${API}/api/dev-calendar/suggestions/generate`,
        {},
        { headers: getHeaders() }
      );
      toast.success(`Generated ${res.data.generated_count} suggestions`);
      fetchData();
    } catch (error) {
      toast.error('Failed to generate suggestions');
    }
  };

  const handleConvertSuggestion = async (suggestion, date, startTime, endTime) => {
    try {
      await axios.post(
        `${API}/api/dev-calendar/suggestions/${suggestion.id}/convert`,
        null,
        { 
          headers: getHeaders(),
          params: { date, start_time: startTime, end_time: endTime }
        }
      );
      toast.success('Converted to task');
      fetchData();
    } catch (error) {
      toast.error('Failed to convert suggestion');
    }
  };

  // Form helpers
  const resetTaskForm = () => {
    setTaskForm({
      title: '',
      description: '',
      task_type: 'feature',
      priority: 'medium',
      date: '',
      start_time: '12:30',
      end_time: '14:00',
      assigned_agent_id: '',
      scope_item_id: '',
      timeline_milestone_id: '',
      auto_create_scope: false,
      // Definition Tab
      acceptance_criteria: '',
      expected_outcome: '',
      // Technical Tab
      technical_requirements: '',
      related_files: [],
      ui_requirements: '',
      // Testing Tab
      testing_instructions: '',
      test_scenarios: '',
      success_indicators: '',
      // Context Tab
      dependencies_notes: '',
      reference_links: []
    });
    setActiveTab('definition');
  };

  const resetAgentForm = () => {
    setAgentForm({
      name: '',
      description: '',
      specialty: 'general',
      avatar_color: '#6366f1',
      work_hours: {
        start_time: '12:30',
        end_time: '21:30',
        days_of_week: [1, 2, 3, 4, 5]
      },
      external_id: '',
      external_type: ''
    });
  };

  const openEditTask = (task) => {
    setEditingTask(task);
    setTaskForm({
      title: task.title || '',
      description: task.description || '',
      task_type: task.task_type || 'feature',
      priority: task.priority || 'medium',
      date: task.date || '',
      start_time: task.start_time || '12:30',
      end_time: task.end_time || '14:00',
      assigned_agent_id: task.assigned_agent_id || '',
      scope_item_id: task.scope_item_id || '',
      timeline_milestone_id: task.timeline_milestone_id || '',
      auto_create_scope: false,
      // Definition Tab
      acceptance_criteria: task.acceptance_criteria || '',
      expected_outcome: task.expected_outcome || '',
      // Technical Tab
      technical_requirements: task.technical_requirements || '',
      related_files: task.related_files || [],
      ui_requirements: task.ui_requirements || '',
      // Testing Tab
      testing_instructions: task.testing_instructions || '',
      test_scenarios: task.test_scenarios || '',
      success_indicators: task.success_indicators || '',
      // Context Tab
      dependencies_notes: task.dependencies_notes || '',
      reference_links: task.reference_links || []
    });
    setActiveTab('definition');
    setShowTaskDialog(true);
  };

  const openNewTask = (date, time) => {
    setEditingTask(null);
    resetTaskForm();
    setTaskForm(prev => ({
      ...prev,
      date: date || new Date().toISOString().split('T')[0],
      start_time: time || '12:30',
      end_time: time ? `${(parseInt(time.split(':')[0]) + 1).toString().padStart(2, '0')}:${time.split(':')[1]}` : '14:00'
    }));
    setActiveTab('definition');
    setShowTaskDialog(true);
  };

  // Apply template to form
  const applyTemplate = (template) => {
    setTaskForm(prev => ({
      ...prev,
      task_type: template.task_type || prev.task_type,
      priority: template.priority || prev.priority,
      description: template.template_description || '',
      acceptance_criteria: template.acceptance_criteria || '',
      expected_outcome: template.expected_outcome || '',
      technical_requirements: template.technical_requirements || '',
      related_files: template.related_files || [],
      ui_requirements: template.ui_requirements || '',
      testing_instructions: template.testing_instructions || '',
      test_scenarios: template.test_scenarios || '',
      success_indicators: template.success_indicators || '',
      dependencies_notes: template.dependencies_notes || '',
      reference_links: template.reference_links || []
    }));
    toast.success(`Template "${template.name}" applied`);
  };

  // Save current task as template
  const saveAsTemplate = async () => {
    const templateName = prompt('Enter a name for this template:');
    if (!templateName) return;
    
    try {
      await axios.post(
        `${API}/api/dev-calendar/templates`,
        {
          name: templateName,
          description: `Template for: ${taskForm.title || 'Untitled'}`,
          task_type: taskForm.task_type,
          priority: taskForm.priority,
          template_description: taskForm.description,
          acceptance_criteria: taskForm.acceptance_criteria,
          expected_outcome: taskForm.expected_outcome,
          technical_requirements: taskForm.technical_requirements,
          related_files: taskForm.related_files,
          ui_requirements: taskForm.ui_requirements,
          testing_instructions: taskForm.testing_instructions,
          test_scenarios: taskForm.test_scenarios,
          success_indicators: taskForm.success_indicators,
          dependencies_notes: taskForm.dependencies_notes,
          reference_links: taskForm.reference_links
        },
        { headers: getHeaders() }
      );
      toast.success('Template saved!');
      fetchData();
    } catch (error) {
      toast.error('Failed to save template');
    }
  };

  // Delete template
  const deleteTemplate = async (templateId) => {
    if (!window.confirm('Delete this template?')) return;
    try {
      await axios.delete(`${API}/api/dev-calendar/templates/${templateId}`, { headers: getHeaders() });
      toast.success('Template deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  // Export task to Markdown for AI agents
  const exportTaskToMarkdown = (task) => {
    const agentName = agents.find(a => a.id === task.assigned_agent_id)?.name || 'Unassigned';
    const statusLabel = TASK_STATUSES[task.status]?.label || task.status;
    const priorityLabel = PRIORITIES[task.priority]?.label || task.priority;
    const typeLabel = TASK_TYPES[task.task_type]?.label || task.task_type;
    
    let markdown = `# Task: ${task.title}\n\n`;
    markdown += `## Overview\n`;
    markdown += `- **Type:** ${typeLabel}\n`;
    markdown += `- **Priority:** ${priorityLabel}\n`;
    markdown += `- **Status:** ${statusLabel}\n`;
    markdown += `- **Assigned To:** ${agentName}\n`;
    markdown += `- **Date:** ${task.date}\n`;
    markdown += `- **Time:** ${formatTime(task.start_time)} - ${formatTime(task.end_time)}\n`;
    if (task.scope_item_name) markdown += `- **Scope Item:** ${task.scope_item_name}\n`;
    if (task.timeline_milestone_name) markdown += `- **Milestone:** ${task.timeline_milestone_name}\n`;
    markdown += `\n`;

    if (task.description) {
      markdown += `## Description\n${task.description}\n\n`;
    }

    if (task.acceptance_criteria) {
      markdown += `## Acceptance Criteria\n${task.acceptance_criteria}\n\n`;
    }

    if (task.expected_outcome) {
      markdown += `## Expected Outcome\n${task.expected_outcome}\n\n`;
    }

    if (task.technical_requirements) {
      markdown += `## Technical Requirements\n${task.technical_requirements}\n\n`;
    }

    if (task.related_files?.length > 0) {
      markdown += `## Related Files\n`;
      task.related_files.forEach(file => {
        markdown += `- \`${file}\`\n`;
      });
      markdown += `\n`;
    }

    if (task.ui_requirements) {
      markdown += `## UI/UX Requirements\n${task.ui_requirements}\n\n`;
    }

    if (task.testing_instructions) {
      markdown += `## Testing Instructions\n${task.testing_instructions}\n\n`;
    }

    if (task.test_scenarios) {
      markdown += `## Test Scenarios / Edge Cases\n${task.test_scenarios}\n\n`;
    }

    if (task.success_indicators) {
      markdown += `## Success Indicators\n${task.success_indicators}\n\n`;
    }

    if (task.dependencies_notes) {
      markdown += `## Dependencies / Blockers\n${task.dependencies_notes}\n\n`;
    }

    if (task.reference_links?.length > 0) {
      markdown += `## Reference Links\n`;
      task.reference_links.forEach(link => {
        markdown += `- ${link}\n`;
      });
      markdown += `\n`;
    }

    // Copy to clipboard
    navigator.clipboard.writeText(markdown).then(() => {
      toast.success('Task specification copied to clipboard!');
    }).catch(() => {
      // Fallback for older browsers
      const textarea = document.createElement('textarea');
      textarea.value = markdown;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      toast.success('Task specification copied to clipboard!');
    });
  };

  const openEditAgent = (agent) => {
    setEditingAgent(agent);
    setAgentForm({
      name: agent.name || '',
      description: agent.description || '',
      specialty: agent.specialty || 'general',
      avatar_color: agent.avatar_color || '#6366f1',
      work_hours: agent.work_hours || {
        start_time: '12:30',
        end_time: '21:30',
        days_of_week: [1, 2, 3, 4, 5]
      },
      external_id: agent.external_id || '',
      external_type: agent.external_type || ''
    });
    setShowAgentDialog(true);
  };

  const timeSlots = generateTimeSlots(
    settings.default_start_time,
    settings.after_hours_end,
    settings.after_hours_start
  );

  // Draggable Task Card component
  const DraggableTaskCard = ({ task, compact = false }) => {
    const { attributes, listeners, setNodeRef, transform, isDragging: isThisDragging } = useDraggable({
      id: task.id,
    });
    
    const TypeIcon = TASK_TYPES[task.task_type]?.icon || Clock;
    const typeConfig = TASK_TYPES[task.task_type] || TASK_TYPES.other;
    const statusConfig = TASK_STATUSES[task.status] || TASK_STATUSES.scheduled;
    
    const style = transform ? {
      transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      zIndex: 1000,
    } : undefined;
    
    return (
      <div 
        ref={setNodeRef}
        style={style}
        onClick={(e) => { e.stopPropagation(); openTaskDetails(task); }}
        className={`${typeConfig.color} bg-opacity-20 border-l-4 ${typeConfig.color} rounded-r-lg p-2 mb-1 cursor-pointer hover:bg-opacity-30 transition-all ${
          task.is_after_hours ? 'border-dashed' : ''
        } ${isThisDragging ? 'opacity-50 shadow-lg ring-2 ring-indigo-500' : ''}`}
      >
        <div className="flex items-center justify-between gap-1">
          <div 
            className="flex items-center gap-1 min-w-0 flex-1"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-3 w-3 flex-shrink-0 text-muted-foreground cursor-grab" />
            <TypeIcon className="h-3 w-3 flex-shrink-0" />
            <span className={`text-xs font-medium truncate ${statusConfig.color}`}>
              {task.title}
            </span>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="sm" className="h-5 w-5 p-0">
                <MoreHorizontal className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openEditTask(task); }}>
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleTaskStatusChange(task.id, 'in_progress'); }}>
                Start
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleTaskStatusChange(task.id, 'completed'); }}>
                Complete
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleTaskStatusChange(task.id, 'blocked'); }}>
                Mark Blocked
              </DropdownMenuItem>
              <DropdownMenuItem 
                className="text-red-500"
                onClick={(e) => { e.stopPropagation(); handleDeleteTask(task.id); }}
              >
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        {!compact && (
          <div className="flex items-center gap-2 mt-1 text-[10px] text-muted-foreground">
            <span>{formatTime(task.start_time)} - {formatTime(task.end_time)}</span>
            {task.assigned_agent_name && (
              <Badge variant="outline" className="text-[9px] h-4 px-1">
                <Bot className="h-2 w-2 mr-0.5" />
                {task.assigned_agent_name}
              </Badge>
            )}
          </div>
        )}
      </div>
    );
  };

  // Droppable Cell component
  const DroppableCell = ({ dateStr, time, isAfterHours, children, onClick }) => {
    const dropId = `${dateStr}-${time}`;
    const { isOver, setNodeRef } = useDroppable({
      id: dropId,
    });
    
    return (
      <div 
        ref={setNodeRef}
        className={`min-h-[40px] border-b border-r p-0.5 cursor-pointer transition-colors ${
          isAfterHours ? 'bg-purple-50/50 dark:bg-purple-900/5' : ''
        } ${isOver ? 'bg-indigo-100 dark:bg-indigo-900/30 ring-2 ring-inset ring-indigo-500' : 'hover:bg-muted/30'}`}
        onClick={onClick}
      >
        {children}
      </div>
    );
  };

  // Drag overlay task preview
  const TaskDragOverlay = ({ task }) => {
    if (!task) return null;
    const TypeIcon = TASK_TYPES[task.task_type]?.icon || Clock;
    const typeConfig = TASK_TYPES[task.task_type] || TASK_TYPES.other;
    
    return (
      <div className={`${typeConfig.color} bg-opacity-40 border-l-4 ${typeConfig.color} rounded-r-lg p-2 shadow-xl w-48`}>
        <div className="flex items-center gap-1">
          <GripVertical className="h-3 w-3" />
          <TypeIcon className="h-3 w-3" />
          <span className="text-xs font-medium truncate">{task.title}</span>
        </div>
        <div className="text-[10px] text-muted-foreground mt-1">
          {formatTime(task.start_time)} - {formatTime(task.end_time)}
        </div>
      </div>
    );
  };

  // Legacy TaskCard for non-draggable contexts (like daily view expanded)
  const TaskCard = ({ task, compact = false }) => {
    const TypeIcon = TASK_TYPES[task.task_type]?.icon || Clock;
    const typeConfig = TASK_TYPES[task.task_type] || TASK_TYPES.other;
    const priorityConfig = PRIORITIES[task.priority] || PRIORITIES.medium;
    const statusConfig = TASK_STATUSES[task.status] || TASK_STATUSES.scheduled;
    
    return (
      <div 
        className={`${typeConfig.color} bg-opacity-20 border-l-4 ${typeConfig.color} rounded-r-lg p-2 mb-1 cursor-pointer hover:bg-opacity-30 transition-all ${
          task.is_after_hours ? 'border-dashed' : ''
        }`}
        onClick={() => openTaskDetails(task)}
      >
        <div className="flex items-center justify-between gap-1">
          <div className="flex items-center gap-1 min-w-0 flex-1">
            <TypeIcon className="h-3 w-3 flex-shrink-0" />
            <span className={`text-xs font-medium truncate ${statusConfig.color}`}>
              {task.title}
            </span>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="sm" className="h-5 w-5 p-0">
                <MoreHorizontal className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleTaskStatusChange(task.id, 'in_progress'); }}>
                Start
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleTaskStatusChange(task.id, 'completed'); }}>
                Complete
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleTaskStatusChange(task.id, 'blocked'); }}>
                Mark Blocked
              </DropdownMenuItem>
              <DropdownMenuItem 
                className="text-red-500"
                onClick={(e) => { e.stopPropagation(); handleDeleteTask(task.id); }}
              >
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        {!compact && (
          <div className="flex items-center gap-2 mt-1 text-[10px] text-muted-foreground">
            <span>{formatTime(task.start_time)} - {formatTime(task.end_time)}</span>
            {task.assigned_agent_name && (
              <Badge variant="outline" className="text-[9px] h-4 px-1">
                <Bot className="h-2 w-2 mr-0.5" />
                {task.assigned_agent_name}
              </Badge>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <Layout>
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Calendar className="h-6 w-6 text-indigo-500" />
            Development Calendar
          </h1>
          <p className="text-sm text-muted-foreground">
            Plan your builds, testing, and deployments • Work hours: {formatTime(settings.default_start_time)} - {formatTime(settings.default_end_time)}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* View Toggle */}
          <div className="flex items-center bg-muted rounded-lg p-1">
            <Button
              variant={view === 'weekly' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setView('weekly')}
              className="h-7"
            >
              Week
            </Button>
            <Button
              variant={view === 'daily' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setView('daily')}
              className="h-7"
            >
              Day
            </Button>
          </div>
          
          <Button variant="outline" size="sm" onClick={goToToday}>
            Today
          </Button>
          
          <Button variant="outline" size="icon" onClick={() => setShowAgentDialog(true)}>
            <Bot className="h-4 w-4" />
          </Button>
          
          <Button variant="outline" size="icon" onClick={() => setShowSettingsDialog(true)}>
            <Settings className="h-4 w-4" />
          </Button>
          
          <Button onClick={() => openNewTask()}>
            <Plus className="h-4 w-4 mr-1" />
            Add Task
          </Button>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => view === 'weekly' ? navigateWeek(-1) : navigateDay(-1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="icon" onClick={() => view === 'weekly' ? navigateWeek(1) : navigateDay(1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <h2 className="text-lg font-semibold ml-2">
            {view === 'weekly' 
              ? `${formatDateShort(weekDates[0].toISOString().split('T')[0])} - ${formatDateShort(weekDates[6].toISOString().split('T')[0])}, ${weekDates[0].getFullYear()}`
              : currentDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })
            }
          </h2>
        </div>
        
        {/* Agents Quick View */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Agents:</span>
          {agents.map(agent => (
            <Badge 
              key={agent.id}
              variant="outline"
              className="cursor-pointer hover:bg-muted"
              style={{ borderColor: agent.avatar_color }}
              onClick={() => openEditAgent(agent)}
            >
              <Bot className="h-3 w-3 mr-1" style={{ color: agent.avatar_color }} />
              {agent.name}
            </Badge>
          ))}
          <Button variant="ghost" size="sm" onClick={() => { setEditingAgent(null); resetAgentForm(); setShowAgentDialog(true); }}>
            <Plus className="h-3 w-3" />
          </Button>
        </div>
      </div>

      {/* Calendar Grid */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <div className="flex gap-4">
        {/* Main Calendar */}
        <div className="flex-1 border rounded-lg overflow-hidden">
          {view === 'weekly' ? (
            // Weekly View
            <div className="grid grid-cols-8">
              {/* Time column header */}
              <div className="bg-muted/50 p-2 border-b border-r">
                <span className="text-xs font-medium text-muted-foreground">Time</span>
              </div>
              {/* Day headers */}
              {weekDates.map((date, idx) => {
                const dateStr = date.toISOString().split('T')[0];
                const isToday = dateStr === new Date().toISOString().split('T')[0];
                const dayTasks = tasksByDate[dateStr] || [];
                return (
                  <div 
                    key={idx}
                    className={`bg-muted/50 p-2 border-b text-center ${isToday ? 'bg-indigo-100 dark:bg-indigo-900/30' : ''}`}
                  >
                    <p className="text-xs text-muted-foreground">
                      {date.toLocaleDateString('en-US', { weekday: 'short' })}
                    </p>
                    <p className={`text-lg font-bold ${isToday ? 'text-indigo-600' : ''}`}>
                      {date.getDate()}
                    </p>
                    <Badge variant="secondary" className="text-[9px]">
                      {dayTasks.length} tasks
                    </Badge>
                  </div>
                );
              })}
              
              {/* Time slots */}
              {timeSlots.map((slot, slotIdx) => (
                <React.Fragment key={slot.time}>
                  {/* Time label */}
                  <div className={`p-1 border-r text-right ${slot.isAfterHours ? 'bg-purple-50 dark:bg-purple-900/10' : ''}`}>
                    <span className="text-[10px] text-muted-foreground">
                      {formatTime(slot.time)}
                    </span>
                    {slot.isAfterHours && slotIdx === timeSlots.findIndex(s => s.isAfterHours) && (
                      <div className="flex items-center justify-end gap-1 mt-0.5">
                        <Moon className="h-2 w-2 text-purple-400" />
                        <span className="text-[8px] text-purple-400">After Hours</span>
                      </div>
                    )}
                  </div>
                  {/* Day cells - now droppable */}
                  {weekDates.map((date, dayIdx) => {
                    const dateStr = date.toISOString().split('T')[0];
                    const [slotHour, slotMin] = slot.time.split(':').map(Number);
                    const slotMinutes = slotHour * 60 + slotMin;
                    const nextSlotMinutes = slotMinutes + 30;
                    const cellTasks = (tasksByDate[dateStr] || []).filter(t => {
                      if (!t.start_time) return false;
                      const [taskHour, taskMin] = t.start_time.split(':').map(Number);
                      const taskMinutes = taskHour * 60 + taskMin;
                      return taskMinutes >= slotMinutes && taskMinutes < nextSlotMinutes;
                    });
                    return (
                      <DroppableCell
                        key={`${dateStr}-${slot.time}`}
                        dateStr={dateStr}
                        time={slot.time}
                        isAfterHours={slot.isAfterHours}
                        onClick={() => openNewTask(dateStr, slot.time)}
                      >
                        {cellTasks.map(task => (
                          <DraggableTaskCard key={task.id} task={task} compact />
                        ))}
                      </DroppableCell>
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
          ) : (
            // Daily View
            <div className="grid grid-cols-1">
              {/* Day header */}
              <div className="bg-muted/50 p-4 border-b">
                <p className="text-2xl font-bold">
                  {currentDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                </p>
                <p className="text-sm text-muted-foreground">
                  {(tasksByDate[currentDate.toISOString().split('T')[0]] || []).length} tasks scheduled
                </p>
              </div>
              
              {/* Time slots */}
              <ScrollArea className="h-[600px]">
                {timeSlots.map((slot, idx) => {
                  const dateStr = currentDate.toISOString().split('T')[0];
                  const [slotHour, slotMin] = slot.time.split(':').map(Number);
                  const slotMinutes = slotHour * 60 + slotMin;
                  const nextSlotMinutes = slotMinutes + 30;
                  
                  // Find tasks that start within this 30-minute slot
                  const slotTasks = (tasksByDate[dateStr] || []).filter(t => {
                    if (!t.start_time) return false;
                    const [taskHour, taskMin] = t.start_time.split(':').map(Number);
                    const taskMinutes = taskHour * 60 + taskMin;
                    return taskMinutes >= slotMinutes && taskMinutes < nextSlotMinutes;
                  });
                  return (
                    <div 
                      key={slot.time}
                      className={`flex border-b ${slot.isAfterHours ? 'bg-purple-50/50 dark:bg-purple-900/5' : ''}`}
                    >
                      <div className="w-24 p-3 border-r flex-shrink-0">
                        <span className="text-sm font-medium">{formatTime(slot.time)}</span>
                        {slot.isAfterHours && idx === timeSlots.findIndex(s => s.isAfterHours) && (
                          <div className="flex items-center gap-1 mt-1">
                            <Moon className="h-3 w-3 text-purple-400" />
                            <span className="text-[10px] text-purple-400">After Hours</span>
                          </div>
                        )}
                      </div>
                      <div 
                        className="flex-1 p-2 min-h-[60px] cursor-pointer hover:bg-muted/30"
                        onClick={() => openNewTask(dateStr, slot.time)}
                      >
                        {slotTasks.map(task => (
                          <TaskCard key={task.id} task={task} />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </ScrollArea>
            </div>
          )}
        </div>

        {/* Suggestions Sidebar */}
        <div className="w-72 border rounded-lg p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold flex items-center gap-2">
              <Moon className="h-4 w-4 text-purple-500" />
              After Hours Ideas
            </h3>
            <Button variant="ghost" size="sm" onClick={handleGenerateSuggestions}>
              <Sparkles className="h-3 w-3 mr-1" />
              Generate
            </Button>
          </div>
          
          <ScrollArea className="h-[400px]">
            <div className="space-y-2 pr-2">
              {suggestions.length > 0 ? suggestions.map(suggestion => {
                const TypeIcon = TASK_TYPES[suggestion.task_type]?.icon || Clock;
                const priorityConfig = PRIORITIES[suggestion.priority] || PRIORITIES.medium;
                return (
                  <div 
                    key={suggestion.id}
                    className="p-3 bg-muted/50 rounded-lg border border-dashed border-purple-300 dark:border-purple-700"
                  >
                    <div className="flex items-start gap-2">
                      <TypeIcon className="h-4 w-4 text-purple-500 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{suggestion.title}</p>
                        <p className="text-[10px] text-muted-foreground">{suggestion.reason}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <Badge variant="outline" className={`text-[9px] ${priorityConfig.color} text-white`}>
                            {priorityConfig.label}
                          </Badge>
                          <span className="text-[10px] text-muted-foreground">~{suggestion.estimated_hours}h</span>
                          {suggestion.is_ai_generated && (
                            <Sparkles className="h-3 w-3 text-purple-400" />
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-1 mt-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="h-6 text-[10px] flex-1"
                        onClick={() => {
                          const today = new Date().toISOString().split('T')[0];
                          handleConvertSuggestion(suggestion, today, '21:30', '23:00');
                        }}
                      >
                        Schedule Tonight
                      </Button>
                    </div>
                  </div>
                );
              }) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No suggestions yet. Click Generate to get AI recommendations based on your scope items.
                </p>
              )}
            </div>
          </ScrollArea>
          
          <Separator />
          
          {/* Quick Add Suggestion */}
          <Button 
            variant="outline" 
            className="w-full"
            onClick={() => {
              // Open dialog to add manual suggestion
              toast.info('Manual suggestion feature coming soon');
            }}
          >
            <Plus className="h-4 w-4 mr-1" />
            Add to Overflow List
          </Button>
        </div>
      </div>
      
      {/* Drag Overlay - shows while dragging */}
      <DragOverlay>
        {activeTask ? <TaskDragOverlay task={activeTask} /> : null}
      </DragOverlay>
      </DndContext>

      {/* Task Dialog - Comprehensive Feature Specification */}
      <Dialog open={showTaskDialog} onOpenChange={setShowTaskDialog}>
        <DialogContent className="max-w-4xl w-[95vw] max-h-[90vh] overflow-hidden p-0" hideCloseButton>
          <div className="flex flex-col h-full max-h-[90vh]">
            {/* Header */}
            <div className="p-4 border-b bg-muted/30">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold">{editingTask ? 'Edit Task' : 'New Task'}</h2>
                  <p className="text-sm text-muted-foreground">Complete feature specification for AI agents</p>
                </div>
                <div className="flex items-center gap-2">
                  {templates.length > 0 && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm">
                          <Copy className="h-4 w-4 mr-1" />
                          Templates
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56">
                        {templates.map(template => (
                          <DropdownMenuItem 
                            key={template.id}
                            className="flex items-center justify-between"
                          >
                            <span 
                              className="flex-1 cursor-pointer"
                              onClick={() => applyTemplate(template)}
                            >
                              {template.name}
                            </span>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-6 w-6 p-0 hover:bg-destructive hover:text-destructive-foreground"
                              onClick={(e) => { e.stopPropagation(); deleteTemplate(template.id); }}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                  <Button variant="outline" size="sm" onClick={saveAsTemplate}>
                    <Save className="h-4 w-4 mr-1" />
                    Save Template
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => { setShowTaskDialog(false); setEditingTask(null); resetTaskForm(); }}
                    className="h-8 w-8 p-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Content with Tabs */}
            <div className="flex-1 overflow-hidden">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
                <div className="px-4 pt-2 border-b">
                  <TabsList className="grid w-full grid-cols-4">
                    <TabsTrigger value="definition" className="flex items-center gap-1">
                      <FileText className="h-3 w-3" />
                      Definition
                    </TabsTrigger>
                    <TabsTrigger value="technical" className="flex items-center gap-1">
                      <FileCode className="h-3 w-3" />
                      Technical
                    </TabsTrigger>
                    <TabsTrigger value="testing" className="flex items-center gap-1">
                      <TestTube className="h-3 w-3" />
                      Testing
                    </TabsTrigger>
                    <TabsTrigger value="context" className="flex items-center gap-1">
                      <Link className="h-3 w-3" />
                      Context
                    </TabsTrigger>
                  </TabsList>
                </div>

                <ScrollArea className="flex-1 p-4">
                  {/* Definition Tab */}
                  <TabsContent value="definition" className="mt-0 space-y-4">
                    {/* Basic Info Row */}
                    <div className="grid grid-cols-4 gap-3">
                      <div className="col-span-2">
                        <label className="text-sm font-medium">Title *</label>
                        <Input
                          value={taskForm.title}
                          onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                          placeholder="Feature name..."
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium">Type</label>
                        <Select value={taskForm.task_type} onValueChange={(v) => setTaskForm({ ...taskForm, task_type: v })}>
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(TASK_TYPES).map(([key, config]) => (
                              <SelectItem key={key} value={key}>
                                <div className="flex items-center gap-2">
                                  <config.icon className="h-3 w-3" />
                                  {config.label}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-sm font-medium">Priority</label>
                        <Select value={taskForm.priority} onValueChange={(v) => setTaskForm({ ...taskForm, priority: v })}>
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(PRIORITIES).map(([key, config]) => (
                              <SelectItem key={key} value={key}>{config.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Schedule Row */}
                    <div className="grid grid-cols-4 gap-3">
                      <div>
                        <label className="text-sm font-medium">Date *</label>
                        <Input
                          type="date"
                          value={taskForm.date}
                          onChange={(e) => setTaskForm({ ...taskForm, date: e.target.value })}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium">Start Time</label>
                        <Input
                          type="time"
                          value={taskForm.start_time}
                          onChange={(e) => setTaskForm({ ...taskForm, start_time: e.target.value })}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium">End Time</label>
                        <Input
                          type="time"
                          value={taskForm.end_time}
                          onChange={(e) => setTaskForm({ ...taskForm, end_time: e.target.value })}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium">Assign Agent</label>
                        <Select 
                          value={taskForm.assigned_agent_id || "none"} 
                          onValueChange={(v) => setTaskForm({ ...taskForm, assigned_agent_id: v === "none" ? "" : v })}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Select..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Unassigned</SelectItem>
                            {agents.map(agent => (
                              <SelectItem key={agent.id} value={agent.id}>
                                <div className="flex items-center gap-2">
                                  <Bot className="h-3 w-3" style={{ color: agent.avatar_color }} />
                                  {agent.name}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Description - Collapsible */}
                    <Collapsible defaultOpen>
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium">Description</label>
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </CollapsibleTrigger>
                      </div>
                      <CollapsibleContent>
                        <Textarea
                          value={taskForm.description}
                          onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                          placeholder="What needs to be built? Describe the feature in detail..."
                          className="mt-1 min-h-[100px]"
                          rows={4}
                        />
                      </CollapsibleContent>
                    </Collapsible>

                    {/* Acceptance Criteria */}
                    <Collapsible defaultOpen>
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium flex items-center gap-2">
                          <ListChecks className="h-4 w-4 text-green-500" />
                          Acceptance Criteria
                        </label>
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </CollapsibleTrigger>
                      </div>
                      <CollapsibleContent>
                        <Textarea
                          value={taskForm.acceptance_criteria}
                          onChange={(e) => setTaskForm({ ...taskForm, acceptance_criteria: e.target.value })}
                          placeholder="- [ ] User can...\n- [ ] System should...\n- [ ] Data must..."
                          className="mt-1 min-h-[80px] font-mono text-sm"
                          rows={4}
                        />
                        <p className="text-xs text-muted-foreground mt-1">Checklist of conditions that must be met for the task to be considered complete</p>
                      </CollapsibleContent>
                    </Collapsible>

                    {/* Expected Outcome */}
                    <Collapsible>
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium">Expected Outcome</label>
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </CollapsibleTrigger>
                      </div>
                      <CollapsibleContent>
                        <Textarea
                          value={taskForm.expected_outcome}
                          onChange={(e) => setTaskForm({ ...taskForm, expected_outcome: e.target.value })}
                          placeholder="When complete, the user will be able to..."
                          className="mt-1"
                          rows={3}
                        />
                      </CollapsibleContent>
                    </Collapsible>
                  </TabsContent>

                  {/* Technical Tab */}
                  <TabsContent value="technical" className="mt-0 space-y-4">
                    {/* Technical Requirements */}
                    <Collapsible defaultOpen>
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium flex items-center gap-2">
                          <FileCode className="h-4 w-4 text-blue-500" />
                          Technical Requirements
                        </label>
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </CollapsibleTrigger>
                      </div>
                      <CollapsibleContent>
                        <Textarea
                          value={taskForm.technical_requirements}
                          onChange={(e) => setTaskForm({ ...taskForm, technical_requirements: e.target.value })}
                          placeholder="Backend: Create API endpoint at /api/...\nFrontend: Add component to...\nDatabase: Create collection for..."
                          className="mt-1 min-h-[120px] font-mono text-sm"
                          rows={6}
                        />
                        <p className="text-xs text-muted-foreground mt-1">Specific implementation details - endpoints, components, data structures</p>
                      </CollapsibleContent>
                    </Collapsible>

                    {/* Related Files */}
                    <Collapsible defaultOpen>
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium">Related Files / Components</label>
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </CollapsibleTrigger>
                      </div>
                      <CollapsibleContent>
                        <Textarea
                          value={taskForm.related_files.join('\n')}
                          onChange={(e) => setTaskForm({ ...taskForm, related_files: e.target.value.split('\n').filter(f => f.trim()) })}
                          placeholder="/app/frontend/src/pages/...\n/app/backend/routes/...\n/app/backend/models/..."
                          className="mt-1 font-mono text-sm"
                          rows={4}
                        />
                        <p className="text-xs text-muted-foreground mt-1">One file path per line - files the agent should reference or modify</p>
                      </CollapsibleContent>
                    </Collapsible>

                    {/* UI Requirements */}
                    <Collapsible>
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium">UI/UX Requirements</label>
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </CollapsibleTrigger>
                      </div>
                      <CollapsibleContent>
                        <Textarea
                          value={taskForm.ui_requirements}
                          onChange={(e) => setTaskForm({ ...taskForm, ui_requirements: e.target.value })}
                          placeholder="Layout: ...\nColors: Match existing theme\nComponents: Use shadcn Button, Card..."
                          className="mt-1"
                          rows={4}
                        />
                        <p className="text-xs text-muted-foreground mt-1">Design specifications, layout requirements, component usage</p>
                      </CollapsibleContent>
                    </Collapsible>
                  </TabsContent>

                  {/* Testing Tab */}
                  <TabsContent value="testing" className="mt-0 space-y-4">
                    {/* Testing Instructions */}
                    <Collapsible defaultOpen>
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium flex items-center gap-2">
                          <TestTube className="h-4 w-4 text-purple-500" />
                          Testing Instructions
                        </label>
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </CollapsibleTrigger>
                      </div>
                      <CollapsibleContent>
                        <Textarea
                          value={taskForm.testing_instructions}
                          onChange={(e) => setTaskForm({ ...taskForm, testing_instructions: e.target.value })}
                          placeholder="1. Navigate to /page\n2. Click on 'Button'\n3. Enter data: ...\n4. Verify that..."
                          className="mt-1 min-h-[120px] font-mono text-sm"
                          rows={6}
                        />
                        <p className="text-xs text-muted-foreground mt-1">Step-by-step instructions for testing this feature</p>
                      </CollapsibleContent>
                    </Collapsible>

                    {/* Test Scenarios */}
                    <Collapsible defaultOpen>
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium">Test Scenarios / Edge Cases</label>
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </CollapsibleTrigger>
                      </div>
                      <CollapsibleContent>
                        <Textarea
                          value={taskForm.test_scenarios}
                          onChange={(e) => setTaskForm({ ...taskForm, test_scenarios: e.target.value })}
                          placeholder="- Empty input validation\n- Large data handling\n- Network error recovery\n- Permission denied scenario"
                          className="mt-1 font-mono text-sm"
                          rows={4}
                        />
                        <p className="text-xs text-muted-foreground mt-1">Edge cases and scenarios that must be verified</p>
                      </CollapsibleContent>
                    </Collapsible>

                    {/* Success Indicators */}
                    <Collapsible>
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium flex items-center gap-2">
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          Success Indicators
                        </label>
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </CollapsibleTrigger>
                      </div>
                      <CollapsibleContent>
                        <Textarea
                          value={taskForm.success_indicators}
                          onChange={(e) => setTaskForm({ ...taskForm, success_indicators: e.target.value })}
                          placeholder="- Toast message shows 'Success'\n- Data appears in table\n- API returns 200 status"
                          className="mt-1"
                          rows={3}
                        />
                        <p className="text-xs text-muted-foreground mt-1">What does "working correctly" look like?</p>
                      </CollapsibleContent>
                    </Collapsible>
                  </TabsContent>

                  {/* Context Tab */}
                  <TabsContent value="context" className="mt-0 space-y-4">
                    {/* Link to Scope */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium">Link to Scope Item</label>
                        <Select 
                          value={taskForm.scope_item_id || "none"} 
                          onValueChange={(v) => setTaskForm({ ...taskForm, scope_item_id: v === "none" ? "" : v })}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Select scope item..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No link</SelectItem>
                            {scopeItems.map(item => (
                              <SelectItem key={item.id} value={item.id}>
                                <div className="flex items-center gap-2">
                                  <Link className="h-3 w-3" />
                                  {item.name}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-sm font-medium">Link to Milestone</label>
                        <Select 
                          value={taskForm.timeline_milestone_id || "none"} 
                          onValueChange={(v) => setTaskForm({ ...taskForm, timeline_milestone_id: v === "none" ? "" : v })}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue placeholder="Select milestone..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No link</SelectItem>
                            {milestones.map(m => (
                              <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {!editingTask && !taskForm.scope_item_id && (
                      <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
                        <input
                          type="checkbox"
                          id="auto-create-scope"
                          checked={taskForm.auto_create_scope}
                          onChange={(e) => setTaskForm({ ...taskForm, auto_create_scope: e.target.checked })}
                          className="rounded"
                        />
                        <label htmlFor="auto-create-scope" className="text-sm">
                          Auto-create scope item from this task
                        </label>
                      </div>
                    )}

                    {/* Dependencies */}
                    <Collapsible defaultOpen>
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium flex items-center gap-2">
                          <AlertCircle className="h-4 w-4 text-orange-500" />
                          Dependencies / Blockers
                        </label>
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </CollapsibleTrigger>
                      </div>
                      <CollapsibleContent>
                        <Textarea
                          value={taskForm.dependencies_notes}
                          onChange={(e) => setTaskForm({ ...taskForm, dependencies_notes: e.target.value })}
                          placeholder="- Requires API from Task X\n- Depends on database schema update\n- Blocked by: ..."
                          className="mt-1"
                          rows={3}
                        />
                        <p className="text-xs text-muted-foreground mt-1">What needs to happen before this task can be completed?</p>
                      </CollapsibleContent>
                    </Collapsible>

                    {/* Reference Links */}
                    <Collapsible>
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium flex items-center gap-2">
                          <ExternalLink className="h-4 w-4 text-blue-500" />
                          Reference Links
                        </label>
                        <CollapsibleTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </CollapsibleTrigger>
                      </div>
                      <CollapsibleContent>
                        <Textarea
                          value={taskForm.reference_links.join('\n')}
                          onChange={(e) => setTaskForm({ ...taskForm, reference_links: e.target.value.split('\n').filter(l => l.trim()) })}
                          placeholder="https://figma.com/...\nhttps://docs.example.com/...\nhttps://github.com/..."
                          className="mt-1 font-mono text-sm"
                          rows={3}
                        />
                        <p className="text-xs text-muted-foreground mt-1">Figma designs, documentation, examples, etc.</p>
                      </CollapsibleContent>
                    </Collapsible>
                  </TabsContent>
                </ScrollArea>
              </Tabs>
            </div>

            {/* Footer */}
            <div className="p-4 border-t bg-muted/30 flex justify-between">
              <Button variant="outline" onClick={() => { setShowTaskDialog(false); setEditingTask(null); resetTaskForm(); }}>
                Cancel
              </Button>
              <Button onClick={editingTask ? handleUpdateTask : handleCreateTask}>
                {editingTask ? 'Update' : 'Create'} Task
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Agent Dialog */}
      <Dialog open={showAgentDialog} onOpenChange={setShowAgentDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingAgent ? 'Edit Agent' : 'New Agent'}</DialogTitle>
            <DialogDescription>Configure a development agent</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Agent Name</label>
              <Input
                value={agentForm.name}
                onChange={(e) => setAgentForm({ ...agentForm, name: e.target.value })}
                placeholder="e.g., Frontend Bot, Backend Agent..."
                className="mt-1"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Specialty</label>
                <Select value={agentForm.specialty} onValueChange={(v) => setAgentForm({ ...agentForm, specialty: v })}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="frontend">Frontend</SelectItem>
                    <SelectItem value="backend">Backend</SelectItem>
                    <SelectItem value="fullstack">Full Stack</SelectItem>
                    <SelectItem value="testing">Testing</SelectItem>
                    <SelectItem value="devops">DevOps</SelectItem>
                    <SelectItem value="ai_ml">AI/ML</SelectItem>
                    <SelectItem value="general">General</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium">Avatar Color</label>
                <Input
                  type="color"
                  value={agentForm.avatar_color}
                  onChange={(e) => setAgentForm({ ...agentForm, avatar_color: e.target.value })}
                  className="mt-1 h-9"
                />
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium">Work Hours</label>
              <div className="grid grid-cols-2 gap-3 mt-1">
                <div>
                  <label className="text-xs text-muted-foreground">Start Time</label>
                  <Input
                    type="time"
                    value={agentForm.work_hours.start_time}
                    onChange={(e) => setAgentForm({ 
                      ...agentForm, 
                      work_hours: { ...agentForm.work_hours, start_time: e.target.value }
                    })}
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">End Time</label>
                  <Input
                    type="time"
                    value={agentForm.work_hours.end_time}
                    onChange={(e) => setAgentForm({ 
                      ...agentForm, 
                      work_hours: { ...agentForm.work_hours, end_time: e.target.value }
                    })}
                  />
                </div>
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium">External Agent ID (Optional)</label>
              <Input
                value={agentForm.external_id}
                onChange={(e) => setAgentForm({ ...agentForm, external_id: e.target.value })}
                placeholder="ID to connect external agent..."
                className="mt-1"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium">External Type</label>
              <Select 
                value={agentForm.external_type || "none"} 
                onValueChange={(v) => setAgentForm({ ...agentForm, external_type: v === "none" ? "" : v })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select type..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None (Manual)</SelectItem>
                  <SelectItem value="emergent">Emergent Agent</SelectItem>
                  <SelectItem value="custom">Custom API</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium">Description</label>
              <Textarea
                value={agentForm.description}
                onChange={(e) => setAgentForm({ ...agentForm, description: e.target.value })}
                placeholder="Agent capabilities and notes..."
                className="mt-1"
                rows={2}
              />
            </div>
          </div>
          
          <div className="flex justify-between mt-4">
            {editingAgent && (
              <Button variant="destructive" onClick={() => { handleDeleteAgent(editingAgent.id); setShowAgentDialog(false); }}>
                <Trash2 className="h-4 w-4 mr-1" />
                Delete
              </Button>
            )}
            <div className="flex gap-2 ml-auto">
              <Button variant="outline" onClick={() => { setShowAgentDialog(false); setEditingAgent(null); resetAgentForm(); }}>
                Cancel
              </Button>
              <Button onClick={editingAgent ? handleUpdateAgent : handleCreateAgent}>
                {editingAgent ? 'Update' : 'Create'} Agent
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Settings Dialog */}
      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Calendar Settings</DialogTitle>
            <DialogDescription>Configure your work hours</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Work Start Time</label>
                <Input
                  type="time"
                  value={settings.default_start_time}
                  onChange={(e) => setSettings({ ...settings, default_start_time: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium">Work End Time</label>
                <Input
                  type="time"
                  value={settings.default_end_time}
                  onChange={(e) => setSettings({ ...settings, default_end_time: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">After Hours Start</label>
                <Input
                  type="time"
                  value={settings.after_hours_start}
                  onChange={(e) => setSettings({ ...settings, after_hours_start: e.target.value })}
                  className="mt-1"
                />
              </div>
              <div>
                <label className="text-sm font-medium">After Hours End</label>
                <Input
                  type="time"
                  value={settings.after_hours_end}
                  onChange={(e) => setSettings({ ...settings, after_hours_end: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>
          </div>
          
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowSettingsDialog(false)}>
              Cancel
            </Button>
            <Button onClick={async () => {
              try {
                await axios.patch(`${API}/api/dev-calendar/settings`, settings, { headers: getHeaders() });
                toast.success('Settings saved');
                setShowSettingsDialog(false);
              } catch (error) {
                toast.error('Failed to save settings');
              }
            }}>
              Save Settings
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Task Details Dialog - Full Specification View */}
      <Dialog open={showTaskDetailsDialog} onOpenChange={setShowTaskDetailsDialog}>
        <DialogContent className="max-w-[95vw] w-full max-h-[90vh] overflow-hidden p-0" hideCloseButton>
          {viewingTask && (
            <div className="flex flex-col h-full max-h-[90vh]">
              {/* Header - Fixed */}
              <div className="p-4 border-b bg-muted/30 flex-shrink-0">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {(() => {
                      const typeConfig = TASK_TYPES[viewingTask.task_type] || TASK_TYPES.feature;
                      const TypeIcon = typeConfig.icon;
                      return <div className={`p-2 rounded-lg ${typeConfig.color}`}><TypeIcon className="h-5 w-5 text-white" /></div>;
                    })()}
                    <div>
                      <h2 className="text-lg font-semibold">{viewingTask.title}</h2>
                      <p className="text-sm text-muted-foreground">Complete feature specification</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => exportTaskToMarkdown(viewingTask)}
                      className="gap-1"
                    >
                      <Copy className="h-4 w-4" />
                      Export Markdown
                    </Button>
                    <Badge className={TASK_STATUSES[viewingTask.status]?.color || ''}>
                      {TASK_STATUSES[viewingTask.status]?.label || viewingTask.status}
                    </Badge>
                    <Badge className={`${PRIORITIES[viewingTask.priority]?.color || ''} text-white`}>
                      {PRIORITIES[viewingTask.priority]?.label || viewingTask.priority}
                    </Badge>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => setShowTaskDetailsDialog(false)}
                      className="h-8 w-8 p-0 ml-2"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Content with Tabs */}
              <div className="flex-1 overflow-hidden">
                <Tabs defaultValue="definition" className="h-full flex flex-col">
                  <div className="px-4 pt-2 border-b">
                    <TabsList className="grid w-full grid-cols-5">
                      <TabsTrigger value="definition" className="flex items-center gap-1">
                        <FileText className="h-3 w-3" />
                        Definition
                      </TabsTrigger>
                      <TabsTrigger value="technical" className="flex items-center gap-1">
                        <FileCode className="h-3 w-3" />
                        Technical
                      </TabsTrigger>
                      <TabsTrigger value="testing" className="flex items-center gap-1">
                        <TestTube className="h-3 w-3" />
                        Testing
                      </TabsTrigger>
                      <TabsTrigger value="context" className="flex items-center gap-1">
                        <Link className="h-3 w-3" />
                        Context
                      </TabsTrigger>
                      <TabsTrigger value="activity" className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Activity
                      </TabsTrigger>
                    </TabsList>
                  </div>

                  <ScrollArea className="flex-1 p-4">
                    {/* Definition Tab */}
                    <TabsContent value="definition" className="mt-0 space-y-4">
                      {/* Info Row */}
                      <div className="flex flex-wrap gap-6 p-3 bg-muted/20 rounded-lg">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground">Type:</span>
                          <span className="text-sm font-medium capitalize">{viewingTask.task_type?.replace('_', ' ')}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground">Date:</span>
                          <span className="text-sm font-medium">{viewingTask.date}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-muted-foreground">Time:</span>
                          <span className="text-sm font-medium">{formatTime(viewingTask.start_time)} - {formatTime(viewingTask.end_time)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Bot className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm text-muted-foreground">Assigned:</span>
                          <span className="text-sm font-medium">
                            {agents.find(a => a.id === viewingTask.assigned_agent_id)?.name || 'Unassigned'}
                          </span>
                        </div>
                        {viewingTask.progress > 0 && (
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground">Progress:</span>
                            <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                              <div className="h-full bg-primary" style={{ width: `${viewingTask.progress}%` }} />
                            </div>
                            <span className="text-sm font-medium">{viewingTask.progress}%</span>
                          </div>
                        )}
                      </div>

                      {/* Description */}
                      <Collapsible defaultOpen>
                        <div className="flex items-center justify-between">
                          <label className="text-sm font-medium">Description</label>
                          <CollapsibleTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                              <ChevronDown className="h-4 w-4" />
                            </Button>
                          </CollapsibleTrigger>
                        </div>
                        <CollapsibleContent>
                          <div className="bg-muted/50 p-4 rounded-lg min-h-[80px] whitespace-pre-wrap text-sm mt-1">
                            {viewingTask.description || <span className="text-muted-foreground italic">No description provided</span>}
                          </div>
                        </CollapsibleContent>
                      </Collapsible>

                      {/* Acceptance Criteria */}
                      <Collapsible defaultOpen={!!viewingTask.acceptance_criteria}>
                        <div className="flex items-center justify-between">
                          <label className="text-sm font-medium flex items-center gap-2">
                            <ListChecks className="h-4 w-4 text-green-500" />
                            Acceptance Criteria
                          </label>
                          <CollapsibleTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                              <ChevronDown className="h-4 w-4" />
                            </Button>
                          </CollapsibleTrigger>
                        </div>
                        <CollapsibleContent>
                          <div className="bg-muted/50 p-4 rounded-lg whitespace-pre-wrap text-sm font-mono mt-1">
                            {viewingTask.acceptance_criteria || <span className="text-muted-foreground italic font-sans">No acceptance criteria defined</span>}
                          </div>
                        </CollapsibleContent>
                      </Collapsible>

                      {/* Expected Outcome */}
                      {viewingTask.expected_outcome && (
                        <Collapsible defaultOpen>
                          <div className="flex items-center justify-between">
                            <label className="text-sm font-medium">Expected Outcome</label>
                            <CollapsibleTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                                <ChevronDown className="h-4 w-4" />
                              </Button>
                            </CollapsibleTrigger>
                          </div>
                          <CollapsibleContent>
                            <div className="bg-muted/50 p-4 rounded-lg whitespace-pre-wrap text-sm mt-1">
                              {viewingTask.expected_outcome}
                            </div>
                          </CollapsibleContent>
                        </Collapsible>
                      )}
                    </TabsContent>

                    {/* Technical Tab */}
                    <TabsContent value="technical" className="mt-0 space-y-4">
                      {/* Technical Requirements */}
                      <Collapsible defaultOpen>
                        <div className="flex items-center justify-between">
                          <label className="text-sm font-medium flex items-center gap-2">
                            <FileCode className="h-4 w-4 text-blue-500" />
                            Technical Requirements
                          </label>
                          <CollapsibleTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                              <ChevronDown className="h-4 w-4" />
                            </Button>
                          </CollapsibleTrigger>
                        </div>
                        <CollapsibleContent>
                          <div className="bg-muted/50 p-4 rounded-lg whitespace-pre-wrap text-sm font-mono mt-1 min-h-[100px]">
                            {viewingTask.technical_requirements || <span className="text-muted-foreground italic font-sans">No technical requirements specified</span>}
                          </div>
                        </CollapsibleContent>
                      </Collapsible>

                      {/* Related Files */}
                      <Collapsible defaultOpen={viewingTask.related_files?.length > 0}>
                        <div className="flex items-center justify-between">
                          <label className="text-sm font-medium">Related Files / Components</label>
                          <CollapsibleTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                              <ChevronDown className="h-4 w-4" />
                            </Button>
                          </CollapsibleTrigger>
                        </div>
                        <CollapsibleContent>
                          {viewingTask.related_files?.length > 0 ? (
                            <div className="bg-muted/50 p-4 rounded-lg mt-1 space-y-1">
                              {viewingTask.related_files.map((file, idx) => (
                                <div key={idx} className="text-sm font-mono text-blue-600 dark:text-blue-400">
                                  {file}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="bg-muted/50 p-4 rounded-lg mt-1">
                              <span className="text-muted-foreground italic text-sm">No related files specified</span>
                            </div>
                          )}
                        </CollapsibleContent>
                      </Collapsible>

                      {/* UI Requirements */}
                      {viewingTask.ui_requirements && (
                        <Collapsible defaultOpen>
                          <div className="flex items-center justify-between">
                            <label className="text-sm font-medium">UI/UX Requirements</label>
                            <CollapsibleTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                                <ChevronDown className="h-4 w-4" />
                              </Button>
                            </CollapsibleTrigger>
                          </div>
                          <CollapsibleContent>
                            <div className="bg-muted/50 p-4 rounded-lg whitespace-pre-wrap text-sm mt-1">
                              {viewingTask.ui_requirements}
                            </div>
                          </CollapsibleContent>
                        </Collapsible>
                      )}
                    </TabsContent>

                    {/* Testing Tab */}
                    <TabsContent value="testing" className="mt-0 space-y-4">
                      {/* Testing Instructions */}
                      <Collapsible defaultOpen>
                        <div className="flex items-center justify-between">
                          <label className="text-sm font-medium flex items-center gap-2">
                            <TestTube className="h-4 w-4 text-purple-500" />
                            Testing Instructions
                          </label>
                          <CollapsibleTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                              <ChevronDown className="h-4 w-4" />
                            </Button>
                          </CollapsibleTrigger>
                        </div>
                        <CollapsibleContent>
                          <div className="bg-muted/50 p-4 rounded-lg whitespace-pre-wrap text-sm font-mono mt-1 min-h-[100px]">
                            {viewingTask.testing_instructions || <span className="text-muted-foreground italic font-sans">No testing instructions provided</span>}
                          </div>
                        </CollapsibleContent>
                      </Collapsible>

                      {/* Test Scenarios */}
                      <Collapsible defaultOpen={!!viewingTask.test_scenarios}>
                        <div className="flex items-center justify-between">
                          <label className="text-sm font-medium">Test Scenarios / Edge Cases</label>
                          <CollapsibleTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                              <ChevronDown className="h-4 w-4" />
                            </Button>
                          </CollapsibleTrigger>
                        </div>
                        <CollapsibleContent>
                          <div className="bg-muted/50 p-4 rounded-lg whitespace-pre-wrap text-sm font-mono mt-1">
                            {viewingTask.test_scenarios || <span className="text-muted-foreground italic font-sans">No test scenarios defined</span>}
                          </div>
                        </CollapsibleContent>
                      </Collapsible>

                      {/* Success Indicators */}
                      <Collapsible defaultOpen={!!viewingTask.success_indicators}>
                        <div className="flex items-center justify-between">
                          <label className="text-sm font-medium flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-500" />
                            Success Indicators
                          </label>
                          <CollapsibleTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                              <ChevronDown className="h-4 w-4" />
                            </Button>
                          </CollapsibleTrigger>
                        </div>
                        <CollapsibleContent>
                          <div className="bg-muted/50 p-4 rounded-lg whitespace-pre-wrap text-sm mt-1">
                            {viewingTask.success_indicators || <span className="text-muted-foreground italic">No success indicators defined</span>}
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    </TabsContent>

                    {/* Context Tab */}
                    <TabsContent value="context" className="mt-0 space-y-4">
                      {/* Linked Items */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="p-3 bg-muted/20 rounded-lg">
                          <p className="text-xs text-muted-foreground mb-1">Linked Scope Item</p>
                          <p className="text-sm font-medium">{viewingTask.scope_item_name || 'None'}</p>
                        </div>
                        <div className="p-3 bg-muted/20 rounded-lg">
                          <p className="text-xs text-muted-foreground mb-1">Linked Milestone</p>
                          <p className="text-sm font-medium">{viewingTask.timeline_milestone_name || 'None'}</p>
                        </div>
                      </div>

                      {/* Dependencies */}
                      <Collapsible defaultOpen={!!viewingTask.dependencies_notes}>
                        <div className="flex items-center justify-between">
                          <label className="text-sm font-medium flex items-center gap-2">
                            <AlertCircle className="h-4 w-4 text-orange-500" />
                            Dependencies / Blockers
                          </label>
                          <CollapsibleTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                              <ChevronDown className="h-4 w-4" />
                            </Button>
                          </CollapsibleTrigger>
                        </div>
                        <CollapsibleContent>
                          <div className="bg-muted/50 p-4 rounded-lg whitespace-pre-wrap text-sm mt-1">
                            {viewingTask.dependencies_notes || <span className="text-muted-foreground italic">No dependencies noted</span>}
                          </div>
                        </CollapsibleContent>
                      </Collapsible>

                      {/* Reference Links */}
                      <Collapsible defaultOpen={viewingTask.reference_links?.length > 0}>
                        <div className="flex items-center justify-between">
                          <label className="text-sm font-medium flex items-center gap-2">
                            <ExternalLink className="h-4 w-4 text-blue-500" />
                            Reference Links
                          </label>
                          <CollapsibleTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                              <ChevronDown className="h-4 w-4" />
                            </Button>
                          </CollapsibleTrigger>
                        </div>
                        <CollapsibleContent>
                          {viewingTask.reference_links?.length > 0 ? (
                            <div className="bg-muted/50 p-4 rounded-lg mt-1 space-y-2">
                              {viewingTask.reference_links.map((link, idx) => (
                                <a 
                                  key={idx} 
                                  href={link} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-sm text-blue-600 dark:text-blue-400 hover:underline block truncate"
                                >
                                  {link}
                                </a>
                              ))}
                            </div>
                          ) : (
                            <div className="bg-muted/50 p-4 rounded-lg mt-1">
                              <span className="text-muted-foreground italic text-sm">No reference links</span>
                            </div>
                          )}
                        </CollapsibleContent>
                      </Collapsible>

                      {/* Blocker Alert */}
                      {viewingTask.blocker && (
                        <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                          <div className="flex items-center gap-2 text-red-500 mb-2">
                            <AlertCircle className="h-4 w-4" />
                            <span className="font-medium">Active Blocker</span>
                            <span className="text-xs ml-auto">Type: {viewingTask.blocker.type} | Severity: {viewingTask.blocker.severity}</span>
                          </div>
                          <p className="text-sm">{viewingTask.blocker.description}</p>
                        </div>
                      )}
                    </TabsContent>

                    {/* Activity Tab */}
                    <TabsContent value="activity" className="mt-0 space-y-4">
                      {/* Completion Summary */}
                      {viewingTask.completion_summary && (
                        <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                          <div className="flex items-center gap-2 text-green-500 mb-2">
                            <CheckCircle className="h-4 w-4" />
                            <span className="font-medium">Completion Summary</span>
                          </div>
                          <p className="text-sm mb-2">{viewingTask.completion_summary}</p>
                          {viewingTask.files_changed?.length > 0 && (
                            <p className="text-xs text-muted-foreground">Files changed: {viewingTask.files_changed.join(', ')}</p>
                          )}
                        </div>
                      )}

                      {/* Activity Logs */}
                      <div>
                        <p className="text-sm font-medium mb-2 flex items-center gap-2">
                          <Clock className="h-4 w-4" />
                          Activity Log
                        </p>
                        {taskActivityLogs.length > 0 ? (
                          <div className="space-y-2 bg-muted/20 rounded-lg p-3">
                            {taskActivityLogs.map((log, idx) => (
                              <div key={log.id || idx} className="flex items-start gap-3 text-sm p-2 bg-background rounded border">
                                <div className="w-2 h-2 mt-1.5 rounded-full bg-primary flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-medium capitalize">{log.activity_type?.replace('_', ' ')}</span>
                                    <span className="text-xs text-muted-foreground">
                                      {new Date(log.created_at).toLocaleString()}
                                    </span>
                                  </div>
                                  <p className="text-muted-foreground">{log.description}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground italic p-3 bg-muted/20 rounded-lg">No activity logged yet</p>
                        )}
                      </div>
                    </TabsContent>
                  </ScrollArea>
                </Tabs>
              </div>

              {/* Footer Actions - Fixed */}
              <div className="p-4 border-t bg-muted/30 flex-shrink-0 flex justify-between">
                <Button variant="outline" onClick={() => {
                  setShowTaskDetailsDialog(false);
                  openEditTask(viewingTask);
                }}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Task
                </Button>
                <div className="flex gap-2">
                  {viewingTask.status !== 'completed' && (
                    <>
                      <Button 
                        variant="outline" 
                        onClick={() => {
                          handleTaskStatusChange(viewingTask.id, 'in_progress');
                          setShowTaskDetailsDialog(false);
                        }}
                      >
                        Start
                      </Button>
                      <Button 
                        onClick={() => {
                          handleTaskStatusChange(viewingTask.id, 'completed');
                          setShowTaskDetailsDialog(false);
                        }}
                      >
                        Complete
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
    </Layout>
  );
}
